import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Camera, X, AlertCircle, RefreshCw } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';

const SCANNER_REGION_ID = 'pdks-qr-scanner-region';

const QrScannerModal = ({ open, onClose, onScan, title = 'QR Kodu Okutun', description }) => {
  const scannerRef = useRef(null);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        if (scannerRef.current.isScanning) {
          await scannerRef.current.stop();
        }
        await scannerRef.current.clear();
      } catch (e) { /* ignore */ }
      scannerRef.current = null;
    }
  };

  const startScanner = async (cameraId) => {
    setError('');
    setStarting(true);
    try {
      await stopScanner();
      const el = document.getElementById(SCANNER_REGION_ID);
      if (!el) throw new Error('Tarayıcı bölgesi yüklenmedi');

      const inst = new Html5Qrcode(SCANNER_REGION_ID, { verbose: false });
      scannerRef.current = inst;

      const config = {
        fps: 10,
        qrbox: { width: 240, height: 240 },
        aspectRatio: 1.0,
      };

      const cameraIdToUse = cameraId || { facingMode: 'environment' };

      await inst.start(
        cameraIdToUse,
        config,
        (decodedText) => {
          // Başarılı tarama
          stopScanner().finally(() => {
            onScan(decodedText);
          });
        },
        () => { /* her frame için ignore */ }
      );
    } catch (e) {
      const msg = e?.message || String(e);
      if (msg.includes('Permission') || msg.includes('NotAllowed')) {
        setError('Kamera izni reddedildi. Tarayıcı ayarlarından izin verin.');
      } else if (msg.includes('NotFound') || msg.includes('camera')) {
        setError('Kamera bulunamadı. Cihazınızda kamera olduğundan emin olun.');
      } else {
        setError(`Kamera başlatılamadı: ${msg}`);
      }
    } finally {
      setStarting(false);
    }
  };

  const initScanner = async () => {
    try {
      const devices = await Html5Qrcode.getCameras();
      setCameras(devices || []);
      // Arka kamerayı (environment) tercih et
      const back = (devices || []).find(d => /back|rear|environment/i.test(d.label || ''));
      const camId = back?.id || devices?.[0]?.id || '';
      setSelectedCamera(camId);
      await startScanner(camId || undefined);
    } catch (e) {
      // Cihaz listesi alınamasa bile environment ile dene
      await startScanner();
    }
  };

  useEffect(() => {
    if (open) {
      // DOM'un dialog içeriğini render etmesi için kısa gecikme
      const t = setTimeout(initScanner, 200);
      return () => {
        clearTimeout(t);
        stopScanner();
      };
    } else {
      stopScanner();
    }
  }, [open]);

  const handleSwitchCamera = async () => {
    if (cameras.length < 2) return;
    const idx = cameras.findIndex(c => c.id === selectedCamera);
    const next = cameras[(idx + 1) % cameras.length];
    setSelectedCamera(next.id);
    await startScanner(next.id);
  };

  const handleClose = async () => {
    await stopScanner();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden bg-slate-900 border-slate-700" data-testid="qr-scanner-modal">
        <DialogHeader className="p-4 pb-2 text-white">
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-cyan-400" />
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="text-cyan-200/80 text-xs">{description}</DialogDescription>
          )}
        </DialogHeader>

        <div className="px-4 pb-4">
          <div className="bg-black rounded-lg overflow-hidden relative" style={{ minHeight: 280 }}>
            <div id={SCANNER_REGION_ID} className="w-full" style={{ minHeight: 280 }} data-testid="qr-scanner-region" />
            {starting && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-white text-sm">
                Kamera başlatılıyor...
              </div>
            )}
            {/* Tarama çerçevesi overlay */}
            {!starting && !error && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="w-60 h-60 border-2 border-cyan-400/60 rounded-lg shadow-[0_0_40px_rgba(34,211,238,0.4)]"></div>
              </div>
            )}
          </div>

          {error && (
            <div className="mt-3 bg-rose-500/20 border border-rose-500/40 text-rose-100 rounded-lg p-3 flex items-start gap-2 text-sm" data-testid="qr-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium mb-1">Hata</p>
                <p className="text-xs">{error}</p>
              </div>
            </div>
          )}

          <div className="mt-3 flex gap-2">
            {cameras.length > 1 && (
              <Button
                variant="outline"
                onClick={handleSwitchCamera}
                className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20"
                data-testid="switch-camera-btn"
              >
                <RefreshCw className="w-4 h-4 mr-1" /> Kamera Değiştir
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="cancel-scanner-btn"
            >
              <X className="w-4 h-4 mr-1" /> İptal
            </Button>
          </div>

          <p className="text-center text-cyan-300/60 text-[11px] mt-3">
            Ofis girişindeki QR kodu çerçeveye yerleştirin
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QrScannerModal;
